import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import ProgressBar from '../../components/ProgressBar';

const RespondParametersPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Get claimed amount from dispute data
  const getClaimedAmount = () => {
    if (!dispute) return 5000;
    return parseFloat(dispute.amountClaimed || 5000);
  };

  const claimedAmount = getClaimedAmount();

  const [maxWillingToPay, setMaxWillingToPay] = useState(Math.round(claimedAmount * 0.5));
  const [idealAmount, setIdealAmount] = useState(Math.round(claimedAmount * 0.3));
  const [paymentAbility, setPaymentAbility] = useState('immediate');
  const [customPaymentDays, setCustomPaymentDays] = useState(30);
  const [additionalConsiderations, setAdditionalConsiderations] = useState('');
  const [willingToNegotiate, setWillingToNegotiate] = useState(true);

  useEffect(() => {
    const fetchDispute = async () => {
      try {
        const data = await api.getRespondView(token);
        setDispute(data);

        // Set default amounts based on claimed amount
        const claimed = parseFloat(data.amountClaimed || 5000);
        setMaxWillingToPay(Math.round(claimed * 0.5));
        setIdealAmount(Math.round(claimed * 0.3));
      } catch (err) {
        setError(err.message || 'Failed to load dispute information');
      } finally {
        setLoading(false);
      }
    };

    fetchDispute();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (idealAmount > maxWillingToPay) {
      setError('Ideal amount cannot be higher than maximum willing to pay');
      return;
    }

    if (maxWillingToPay > 15000 || idealAmount > 15000) {
      setError('Amounts cannot exceed €15,000 (platform limit)');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Calculate payment timeline days
      let paymentDays = 0;
      if (paymentAbility === 'immediate') paymentDays = 0;
      else if (paymentAbility === '7_days') paymentDays = 7;
      else if (paymentAbility === '30_days') paymentDays = 30;
      else if (paymentAbility === '60_days') paymentDays = 60;
      else if (paymentAbility === 'custom') paymentDays = customPaymentDays;

      await api.saveRespondParameters(token, dispute.partyBId, {
        maximumPayable: maxWillingToPay,
        idealAmount: idealAmount,
        paymentTimelineDays: paymentDays,
        paymentPlanAcceptable: willingToNegotiate,
        additionalConsiderations: additionalConsiderations,
        relationshipValue: null,
      });

      navigate(`/respond/${token}/settlement`);
    } catch (err) {
      setError(err.message || 'Failed to save parameters. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !dispute) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card max-w-md w-full">
          <div className="text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <ProgressBar current={3} total={4} phase="Settlement Parameters" />

      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-3">Your Settlement Parameters</h1>
        <p className="text-gray-600">
          These parameters are private and help our AI find fair settlement options.
        </p>
        <p className="text-sm text-gray-500 mt-2">
          The other party will never see these numbers - only the final settlement offer if there's a match
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-6">Settlement Amounts</h2>

          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-gray-700">
              <strong>Amount Claimed:</strong> {formatCurrency(claimedAmount)}
            </p>
          </div>

          {/* Ideal Amount */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Ideal Settlement Amount (What you think is fair)
              </label>
              <span className="text-lg font-bold text-primary-600">
                {formatCurrency(idealAmount)}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={15000}
              step={100}
              value={idealAmount}
              onChange={(e) => setIdealAmount(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
              disabled={submitting}
            />
            <p className="text-xs text-gray-500 mt-2">
              What do you believe would be a fair resolution amount?
            </p>
          </div>

          {/* Maximum Willing to Pay */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Maximum Willing to Pay (Your upper limit)
              </label>
              <span className="text-lg font-bold text-secondary-600">
                {formatCurrency(maxWillingToPay)}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={15000}
              step={100}
              value={maxWillingToPay}
              onChange={(e) => setMaxWillingToPay(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-secondary-500"
              disabled={submitting}
            />
            <p className="text-xs text-gray-500 mt-2">
              What is the absolute maximum you would be willing to pay to settle this?
            </p>
          </div>

          {/* Settlement Range Visualization */}
          <div className="bg-gradient-to-r from-primary-100 via-yellow-100 to-secondary-100 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-gray-600">Your Settlement Zone</span>
              <span className="text-xs font-medium text-gray-600">
                Range: {formatCurrency(maxWillingToPay - idealAmount)}
              </span>
            </div>
            <div className="relative h-8 bg-white rounded-lg overflow-hidden">
              <div
                className="absolute h-full bg-gradient-to-r from-green-400 to-yellow-300 opacity-50"
                style={{
                  left: `${(idealAmount / 15000) * 100}%`,
                  width: `${((maxWillingToPay - idealAmount) / 15000) * 100}%`,
                }}
              />
              <div
                className="absolute top-0 bottom-0 w-1 bg-primary-600"
                style={{ left: `${(idealAmount / 15000) * 100}%` }}
              />
              <div
                className="absolute top-0 bottom-0 w-1 bg-secondary-600"
                style={{ left: `${(maxWillingToPay / 15000) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Payment Ability */}
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4">Payment Ability</h2>
          <p className="text-sm text-gray-600 mb-4">When could you make payment if a settlement is reached?</p>

          <div className="space-y-3">
            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="paymentAbility"
                value="immediate"
                checked={paymentAbility === 'immediate'}
                onChange={(e) => setPaymentAbility(e.target.value)}
                className="mt-1 mr-3"
                disabled={submitting}
              />
              <div>
                <div className="font-medium">Immediate Payment</div>
                <div className="text-sm text-gray-600">Can pay within 24 hours</div>
              </div>
            </label>

            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="paymentAbility"
                value="7_days"
                checked={paymentAbility === '7_days'}
                onChange={(e) => setPaymentAbility(e.target.value)}
                className="mt-1 mr-3"
                disabled={submitting}
              />
              <div>
                <div className="font-medium">Within 7 Days</div>
                <div className="text-sm text-gray-600">Need up to one week</div>
              </div>
            </label>

            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="paymentAbility"
                value="30_days"
                checked={paymentAbility === '30_days'}
                onChange={(e) => setPaymentAbility(e.target.value)}
                className="mt-1 mr-3"
                disabled={submitting}
              />
              <div>
                <div className="font-medium">Within 30 Days</div>
                <div className="text-sm text-gray-600">Need up to one month</div>
              </div>
            </label>

            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="paymentAbility"
                value="60_days"
                checked={paymentAbility === '60_days'}
                onChange={(e) => setPaymentAbility(e.target.value)}
                className="mt-1 mr-3"
                disabled={submitting}
              />
              <div>
                <div className="font-medium">Within 60 Days</div>
                <div className="text-sm text-gray-600">Need up to two months</div>
              </div>
            </label>

            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="paymentAbility"
                value="custom"
                checked={paymentAbility === 'custom'}
                onChange={(e) => setPaymentAbility(e.target.value)}
                className="mt-1 mr-3"
                disabled={submitting}
              />
              <div className="flex-1">
                <div className="font-medium mb-2">Custom Timeline</div>
                {paymentAbility === 'custom' && (
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={customPaymentDays}
                    onChange={(e) => setCustomPaymentDays(parseInt(e.target.value))}
                    className="input w-32"
                    placeholder="Days"
                    disabled={submitting}
                  />
                )}
              </div>
            </label>
          </div>
        </div>

        {/* Additional Considerations */}
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4">Additional Considerations</h2>
          <p className="text-sm text-gray-600 mb-4">
            Any other factors that should be considered? (Optional)
          </p>
          <textarea
            className="input min-h-[100px]"
            placeholder="E.g., payment plan preferences, non-monetary terms, relationship considerations..."
            value={additionalConsiderations}
            onChange={(e) => setAdditionalConsiderations(e.target.value)}
            disabled={submitting}
          />
        </div>

        {/* Negotiation Flexibility */}
        <div className="card mb-6">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={willingToNegotiate}
              onChange={(e) => setWillingToNegotiate(e.target.checked)}
              className="mt-1 mr-3 w-5 h-5 accent-primary-500"
              disabled={submitting}
            />
            <div>
              <div className="font-medium mb-1">I'm willing to negotiate within this range</div>
              <div className="text-sm text-gray-600">
                Our AI will explore settlement options between your ideal and maximum amounts.
                Unchecking this means you'll only accept your ideal amount.
              </div>
            </div>
          </label>
        </div>

        {/* Info Box */}
        <div className="card bg-blue-50 border-blue-200 mb-6">
          <div className="flex items-start">
            <div className="text-2xl mr-3">🔒</div>
            <div>
              <h4 className="font-bold mb-2">Privacy Protection</h4>
              <p className="text-sm text-gray-700 mb-2">
                These parameters are completely private and will never be shown to the other party.
                Our AI uses them to:
              </p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Find settlement zones where both parties might agree</li>
                <li>• Suggest realistic settlement amounts based on similar cases</li>
                <li>• Prioritize solutions within your acceptable range</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            type="button"
            onClick={() => navigate(`/respond/${token}/assessment`)}
            className="btn-secondary"
            disabled={submitting}
          >
            Back
          </button>

          <button
            type="submit"
            className="btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Continue to Settlement Review'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RespondParametersPage;
